// The quotation and invoice links' calls (#512): JSON to the api on this origin, no session; the link's token in the
// body.
import { ApiError, call, type Fetch } from "../http.js";

/** A quotation as its customer sees it through the link. */
export interface QuotationPage {
  readonly business: string;
  readonly number: string;
  readonly date: string;
  readonly validUntil: string;
  readonly customer: string;
  readonly lines: readonly {
    readonly item: string;
    readonly quantity: number;
    readonly unit: string;
    readonly unitPrice: number;
    readonly amount: number;
  }[];
  /** Paise, before GST. */
  readonly total: number;
  readonly pdf: boolean;
}

/** An invoice as its customer sees it through its link. */
export interface InvoicePage {
  readonly business: string;
  readonly number: string;
  readonly date: string;
  readonly customer: string;
  readonly pdf: boolean;
}

export type QuotationAnswer = "accept" | "question" | "decline";

export interface QuotationLinkApi {
  /** The quotation, or null when the link has expired or is not one LAMP made. */
  open(token: string): Promise<QuotationPage | null>;
  /** Its PDF as issued. */
  pdf(token: string): Promise<Blob>;
  answer(token: string, answer: QuotationAnswer, words?: string): Promise<void>;
  /** The invoice an invoice's link names, or null when the link has expired or is not one LAMP made. */
  openInvoice(token: string): Promise<InvoicePage | null>;
  /** The invoice's PDF as issued. */
  invoicePdf(token: string): Promise<Blob>;
}

export function createQuotationLinkApi(fetcher: Fetch = (input, init) => fetch(input, init)): QuotationLinkApi {
  const open = async <T>(path: string, token: string): Promise<T | null> => {
    try {
      return await call<T>(fetcher, path, { json: { token } });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  };
  const pdf = async (path: string, token: string) => {
    const response = await fetcher(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!response.ok) throw new ApiError(response.status, "gone");
    return response.blob();
  };
  return {
    open: (token) => open<QuotationPage>("/api/quotation/open", token),
    pdf: (token) => pdf("/api/quotation/pdf", token),
    openInvoice: (token) => open<InvoicePage>("/api/invoice/open", token),
    invoicePdf: (token) => pdf("/api/invoice/pdf", token),
    async answer(token, answer, words) {
      await call(fetcher, "/api/quotation/answer", { json: { token, answer, ...(words ? { words } : {}) } });
    },
  };
}
